import React, { useState } from "react";
import { Calendar } from "primereact/calendar";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { SelectButton } from "primereact/selectbutton";
import { Tag } from "primereact/tag";
import type { Granularity } from "../../../../types/chargingStationDashboard";
import { elapsedLabel, GRANULARITY_LABEL, lastCompletedAnchor } from "../../../../utils/fleetPeriods";
import { useFleetPeriods } from "../hooks/useFleetPeriods";
import ChargePointsPanel from "./ChargePointsPanel";
import DataFreshness from "./DataFreshness";
import EnergyPanel from "./EnergyPanel";
import GrowthPanel from "./GrowthPanel";
import KpiScorecard from "./KpiScorecard";
import MeasureDefinitions from "./MeasureDefinitions";
import PerSessionPanel from "./PerSessionPanel";
import SessionsPanel from "./SessionsPanel";
import UnavailableNotice from "./UnavailableNotice";

const GRANULARITY_OPTIONS: Array<{ label: string; value: Granularity }> = [
  { label: GRANULARITY_LABEL.MONTH, value: "MONTH" },
  { label: GRANULARITY_LABEL.WEEK, value: "WEEK" },
  { label: GRANULARITY_LABEL.MONTH_ADJUSTED, value: "MONTH_ADJUSTED" },
];

type Section = "fleet" | "kpi";

interface SectionOption {
  label: string;
  value: Section;
  icon: string;
}

const SECTION_OPTIONS: SectionOption[] = [
  { label: "Fleet activity", value: "fleet", icon: "pi pi-bolt" },
  { label: "KPI scorecard", value: "kpi", icon: "pi pi-chart-line" },
];

interface ChargingStationDashboardProps {
  /** Opens the recompute (Station Performance → Data & backfill) — offered when a period was never computed. */
  onRecompute: () => void;
}

/**
 * BOF-76 — the Charging Station Dashboard, replacing the hand-maintained *Charging Station Dashboard*
 * spreadsheet: the fleet's activity, and the monthly KPI scorecard the workbook keeps beside it.
 *
 * The two answer different questions over different periods — the fleet view is weekly or monthly, the
 * scorecard monthly and tied to invoicing — so each keeps its own period picker, and only the section on
 * screen fetches anything.
 */
const ChargingStationDashboard: React.FC<ChargingStationDashboardProps> = ({ onRecompute }) => {
  const [section, setSection] = useState<Section>("fleet");
  return (
    <div>
      <SelectButton
        className="mb-3"
        value={section}
        options={SECTION_OPTIONS}
        optionLabel="label"
        itemTemplate={(o: SectionOption) => (
          <span>
            <i className={`${o.icon} mr-2`} aria-hidden />
            {o.label}
          </span>
        )}
        onChange={(e) => e.value && setSection(e.value)}
        allowEmpty={false}
        aria-label="Dashboard section"
      />
      {section === "fleet" ? <FleetActivity onRecompute={onRecompute} /> : <KpiScorecard onRecompute={onRecompute} />}
    </div>
  );
};

/**
 * The fleet's charge points, energy, sessions and their change.
 *
 * Opens on the most recent **completed** period (FR-003): a running month read against a finished one
 * always looks like a decline. Changing granularity re-anchors to that granularity's last completed
 * period for the same reason.
 *
 * Every figure is fleet-wide — every charge point, every channel — so Station Performance's per-station
 * reporting lenses (unit, segment, money basis, station, channel) do not apply, and this page has none
 * rather than controls that would silently do nothing (FR-004).
 */
const FleetActivity: React.FC<ChargingStationDashboardProps> = ({ onRecompute }) => {
  const [granularity, setGranularity] = useState<Granularity>("MONTH");
  const [anchor, setAnchor] = useState<Date>(() => lastCompletedAnchor("MONTH"));
  const { loading, error, neverComputed, periods, selected, previous, reload } = useFleetPeriods(granularity, anchor);

  const monthly = granularity !== "WEEK";

  const changeGranularity = (next: Granularity) => {
    setGranularity(next);
    // Moving between the two monthly views keeps the month; moving to or from weeks re-anchors.
    if ((next === "WEEK") !== (granularity === "WEEK")) setAnchor(lastCompletedAnchor(next));
  };

  return (
    <div>
      <div className="flex flex-wrap align-items-end justify-content-between gap-3 mb-3">
        <div className="flex flex-wrap align-items-end gap-3">
          <div className="flex flex-column gap-1">
            <label className="text-sm text-color-secondary">View</label>
            <SelectButton
              value={granularity}
              options={GRANULARITY_OPTIONS}
              onChange={(e) => e.value && changeGranularity(e.value)}
              allowEmpty={false}
            />
          </div>
          <div className="flex flex-column gap-1">
            <label className="text-sm text-color-secondary" htmlFor="fleet-period">
              {monthly ? "Month" : "Week containing"}
            </label>
            <Calendar
              inputId="fleet-period"
              value={anchor}
              onChange={(e) => e.value && setAnchor(e.value as Date)}
              view={monthly ? "month" : "date"}
              dateFormat={monthly ? "MM yy" : "dd M yy"}
              maxDate={new Date()}
              showWeek={!monthly}
              showIcon
            />
          </div>
        </div>
        <span className="text-sm text-color-secondary">Fleet-wide: every charge point, every channel.</span>
      </div>

      {loading && (
        <div className="flex justify-content-center p-5">
          <ProgressSpinner style={{ width: 48, height: 48 }} />
        </div>
      )}

      {!loading && neverComputed && <UnavailableNotice reason={{ kind: "neverComputed" }} onRecompute={onRecompute} />}

      {!loading && error && (
        <Message
          severity="error"
          className="w-full justify-content-start"
          content={
            <span>
              {error}{" "}
              <button type="button" className="p-link text-primary ml-2" onClick={reload}>
                Retry
              </button>
            </span>
          }
        />
      )}

      {!loading && !neverComputed && !error && !selected && (
        <Message
          severity="info"
          className="w-full justify-content-start"
          text="No completed days in this period yet. Figures appear once its first day has finished."
        />
      )}

      {!loading && selected && (
        <>
          <div className="flex flex-wrap align-items-center gap-2 mb-3">
            <span className="text-xl font-semibold text-900">{selected.label}</span>
            {selected.adjusted && <Tag value="30-day adjusted" severity="info" />}
            {!selected.complete && <Tag value={`Running · ${elapsedLabel(selected)}`} severity="warning" />}
            <span className="ml-auto">
              <DataFreshness computedAt={selected.computedAt} />
            </span>
          </div>

          {!selected.complete && <UnavailableNotice reason={{ kind: "incomplete", elapsed: elapsedLabel(selected) }} />}
          {selected.partial && <UnavailableNotice reason={{ kind: "partial", missingDays: selected.missingDays }} />}
          {selected.adjusted && (
            <div className="text-sm text-color-secondary mb-3">
              Energy and session figures are scaled to a 30-day month. Charge-point counts and ratios are not.
            </div>
          )}

          <div className="grid">
            <div className="col-12 xl:col-6">
              <ChargePointsPanel selected={selected} previous={previous} trend={periods} />
            </div>
            <div className="col-12 xl:col-6">
              <GrowthPanel selected={selected} previous={previous} />
            </div>
            <div className="col-12 xl:col-6">
              <EnergyPanel selected={selected} previous={previous} trend={periods} />
            </div>
            <div className="col-12 xl:col-6">
              <SessionsPanel selected={selected} previous={previous} trend={periods} />
            </div>
            <div className="col-12 xl:col-6">
              <PerSessionPanel selected={selected} previous={previous} trend={periods} />
            </div>
          </div>
          <MeasureDefinitions />
        </>
      )}
    </div>
  );
};

export default ChargingStationDashboard;
